import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import BundleSection from '../components/bundle/BundleSection';
import UserSecurityModal from '../components/auth/UserSecurityModal';
import {
  getAccountPlanBadgeClass,
  getAccountPlanLabel,
  isBundleAccount,
} from '../utils/accountPlanLabel';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon, 
  UserPlusIcon, 
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  EnvelopeIcon,
  CalendarIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArrowPathIcon,
  NoSymbolIcon,
  LockOpenIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

// Login provider icons and badges
const LoginProviderBadge = ({ provider }) => {
  if (!provider) return null;
  
  const providerConfig = {
    google: {
      label: 'Google',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      icon: (
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      )
    },
    apple: {
      label: 'Apple',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      borderColor: 'border-gray-300',
      icon: (
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
      )
    },
    facebook: {
      label: 'Facebook',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      icon: (
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    }
  };
  
  const config = providerConfig[provider];
  if (!config) return null;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs ${config.bgColor} ${config.textColor} border ${config.borderColor} rounded-full`}>
      {config.icon}
      {config.label}
    </span>
  );
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const PLAN_TO_COMPATIBLE_SITE_TYPES = {
  pro: ['marketing', 'landing_page'],
  landing_page: ['landing_page'],
  ecommerce: ['ecommerce', 'catalog'],
};

const PLAN_TYPE_LABELS = {
  pro: 'Marketing',
  landing_page: 'Landing Page',
  ecommerce: 'eCommerce',
};

const isWebsiteCompatible = (planType, siteType) => {
  const compatibleTypes = PLAN_TO_COMPATIBLE_SITE_TYPES[planType] || [];
  return compatibleTypes.includes(siteType || 'marketing');
};

const formatPlanLabel = (subscription) => {
  if (!subscription) return null;
  const { plan_type: planType, is_bundle: isBundle } = subscription;
  if (isBundle) {
    if (planType === 'pro') return 'Marketing Bundle';
    if (planType === 'ecommerce') return 'Ecommerce Bundle';
    return `${PLAN_TYPE_LABELS[planType] || planType} Bundle`;
  }
  return PLAN_TYPE_LABELS[planType] || planType;
};

const SubscriptionSummary = ({ subscription, siteType }) => {
  if (!subscription) {
    return <span className="text-xs text-gray-400">No active subscription</span>;
  }

  const periodEnd = subscription.current_period_end ? new Date(subscription.current_period_end) : null;
  const isPeriodExpired = periodEnd && periodEnd < new Date();
  const isStale = isPeriodExpired && subscription.status === 'active';
  const hasMismatch = !isWebsiteCompatible(subscription.plan_type, siteType);

  return (
    <div className="mt-1 space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Link
          to={`/subscriptions/${subscription.id}`}
          className="font-medium text-primary-600 hover:text-primary-800"
        >
          {formatPlanLabel(subscription)}
        </Link>
        <span className={`px-2 py-0.5 rounded-full ${
          subscription.status === 'active'
            ? (isStale ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800')
            : 'bg-gray-100 text-gray-700'
        }`}>
          {subscription.status}{isStale ? ' (stale)' : ''}
        </span>
        {subscription.billing_period && (
          <span className="text-gray-500">{subscription.billing_period}</span>
        )}
        {subscription.amount != null && subscription.currency && (
          <span className="text-gray-500">
            {subscription.currency} {subscription.amount}
          </span>
        )}
        {subscription.cancel_at_period_end && (
          <span className="text-amber-700">Cancels at period end</span>
        )}
      </div>
      {hasMismatch && (
        <div className="text-xs text-amber-700 font-medium">
          ⚠️ Site type &quot;{siteType}&quot; doesn&apos;t match subscription plan &quot;{subscription.plan_type}&quot;
        </div>
      )}
    </div>
  );
};

// Trial Management Component
const TrialManagement = ({ account, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [extendDays, setExtendDays] = useState(7);

  const trialStarted = account.trial_started_at;
  const trialExpiresAt = account.trial_expires_at ? new Date(account.trial_expires_at) : null;
  const daysRemaining = trialExpiresAt ? Math.ceil((trialExpiresAt - new Date()) / (1000 * 60 * 60 * 24)) : null;
  // Trial is expired if the flag is set OR if days remaining is 0 or less
  const trialExpired = account.trial_expired || (trialExpiresAt && trialExpiresAt <= new Date());

  const handleResetTrial = async () => {
    if (!window.confirm('Are you sure you want to reset this trial? This will clear all trial data and restart the trial.')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/accounts/${account.id}/trial`, {
        reset_trial: true
      });
      toast.success('Trial has been reset');
      onUpdate();
    } catch (error) {
      console.error('Error resetting trial:', error);
      toast.error(error.response?.data?.error || 'Failed to reset trial');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendTrial = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/accounts/${account.id}/trial`, {
        extend_days: extendDays
      });
      toast.success(`Trial extended by ${extendDays} days`);
      onUpdate();
    } catch (error) {
      console.error('Error extending trial:', error);
      toast.error(error.response?.data?.error || 'Failed to extend trial');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStartDate = async () => {
    if (!newStartDate) {
      toast.error('Please select a date');
      return;
    }
    
    setLoading(true);
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/accounts/${account.id}/trial`, {
        trial_started_at: new Date(newStartDate).toISOString(),
        trial_expired: false
      });
      toast.success('Trial start date updated');
      setEditingDate(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating trial date:', error);
      toast.error(error.response?.data?.error || 'Failed to update trial date');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow mb-8">
      <div className="p-4 border-b flex items-center gap-2">
        <ClockIcon className="h-5 w-5 text-primary-600" />
        <h2 className="text-lg font-semibold">Trial Management</h2>
      </div>
      <div className="p-4">
        {/* Current Trial Status */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <div className="text-sm text-gray-500">Trial Status</div>
            <div className={`font-medium ${trialExpired ? 'text-red-600' : trialStarted ? 'text-green-600' : 'text-gray-600'}`}>
              {trialExpired ? 'Expired' : trialStarted ? 'Active' : 'Not Started'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Started</div>
            <div className="font-medium">
              {trialStarted ? new Date(account.trial_started_at).toLocaleDateString() : 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Expires</div>
            <div className={`font-medium ${trialExpired ? 'text-red-600' : daysRemaining <= 3 ? 'text-orange-600' : ''}`}>
              {trialExpiresAt ? trialExpiresAt.toLocaleDateString() : 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Days Remaining</div>
            <div className={`font-medium ${trialExpired ? 'text-red-600' : daysRemaining <= 3 ? 'text-orange-600' : 'text-green-600'}`}>
              {trialExpired ? '0 (Expired)' : daysRemaining !== null ? `${Math.max(0, daysRemaining)} days` : 'N/A'}
            </div>
          </div>
        </div>

        {/* Edit Start Date */}
        {editingDate ? (
          <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
            <label className="text-sm text-gray-600">New Start Date:</label>
            <input
              type="date"
              value={newStartDate}
              onChange={(e) => setNewStartDate(e.target.value)}
              className="input-field flex-1 max-w-xs"
            />
            <button
              onClick={handleUpdateStartDate}
              disabled={loading}
              className="btn-primary text-sm"
            >
              Save
            </button>
            <button
              onClick={() => setEditingDate(false)}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            {/* Edit Start Date */}
            <button
              onClick={() => {
                setNewStartDate(account.trial_started_at 
                  ? new Date(account.trial_started_at).toISOString().split('T')[0] 
                  : new Date().toISOString().split('T')[0]);
                setEditingDate(true);
              }}
              className="btn-secondary text-sm flex items-center gap-2"
              disabled={loading}
            >
              <PencilIcon className="h-4 w-4" />
              Edit Start Date
            </button>

            {/* Extend Trial */}
            <div className="flex items-center gap-2">
              <select
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value))}
                className="input-field text-sm py-1.5"
              >
                <option value={3}>+3 days</option>
                <option value={7}>+7 days</option>
                <option value={14}>+14 days</option>
                <option value={30}>+30 days</option>
              </select>
              <button
                onClick={handleExtendTrial}
                disabled={loading || !trialStarted}
                className="btn-primary text-sm"
                title={!trialStarted ? 'Trial must be started first' : 'Extend trial period'}
              >
                Extend
              </button>
            </div>

            {/* Reset Trial */}
            <button
              onClick={handleResetTrial}
              disabled={loading}
              className="btn-danger text-sm flex items-center gap-2"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Reset Trial
            </button>
          </div>
        )}

        {/* Sites unpublished info */}
        {account.trial_sites_unpublished_at && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-sm text-red-700">
              <strong>Sites unpublished:</strong> {new Date(account.trial_sites_unpublished_at).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Agency Leads opt-in toggle (enterprise accounts only). Default OFF — when ON,
// the agency starts receiving up to 5 exclusively-assigned CRM leads in its
// /my-sites workspace (agencyLeadAssignmentService refill).
const AGENCY_LEADS_DEFAULT_MAX = 5;
const AGENCY_LEADS_MIN = 1;
const AGENCY_LEADS_MAX = 50;

const AgencyLeadsCard = ({ account, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [savingMax, setSavingMax] = useState(false);
  const enabled = !!account.agency_leads_enabled;
  const effectiveMax = account.agency_leads_max ?? AGENCY_LEADS_DEFAULT_MAX;
  const [maxInput, setMaxInput] = useState(String(effectiveMax));

  // Keep the input in sync when the account reloads (e.g. after a save).
  useEffect(() => {
    setMaxInput(String(account.agency_leads_max ?? AGENCY_LEADS_DEFAULT_MAX));
  }, [account.agency_leads_max]);

  const handleToggle = async () => {
    const next = !enabled;
    setLoading(true);
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/accounts/${account.id}`, {
        agency_leads_enabled: next
      });
      toast.success(next ? 'Agency Leads enabled — this agency will start receiving leads' : 'Agency Leads disabled');
      onUpdate();
    } catch (error) {
      console.error('Error updating agency leads toggle:', error);
      toast.error(error.response?.data?.error || 'Failed to update Agency Leads');
    } finally {
      setLoading(false);
    }
  };

  const saveMax = async (value) => {
    setSavingMax(true);
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/accounts/${account.id}`, {
        agency_leads_max: value
      });
      toast.success(value === null ? `Queue size reset to default (${AGENCY_LEADS_DEFAULT_MAX})` : `Queue size set to ${value}`);
      onUpdate();
    } catch (error) {
      console.error('Error updating agency leads queue size:', error);
      toast.error(error.response?.data?.error || 'Failed to update queue size');
      setMaxInput(String(effectiveMax));
    } finally {
      setSavingMax(false);
    }
  };

  const handleMaxSave = () => {
    const parsed = parseInt(maxInput, 10);
    if (isNaN(parsed) || parsed < AGENCY_LEADS_MIN || parsed > AGENCY_LEADS_MAX) {
      toast.error(`Queue size must be between ${AGENCY_LEADS_MIN} and ${AGENCY_LEADS_MAX}`);
      setMaxInput(String(effectiveMax));
      return;
    }
    if (parsed === effectiveMax) return;
    saveMax(parsed);
  };

  const isOverridden = account.agency_leads_max != null;

  return (
    <div className="bg-white rounded-lg shadow mb-8">
      <div className="p-4 border-b flex items-center gap-2">
        <UserGroupIcon className="h-5 w-5 text-indigo-500" />
        <h2 className="text-lg font-semibold">Agency Leads</h2>
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
          {enabled ? 'ENABLED' : 'DISABLED'}
        </span>
      </div>
      <div className="p-4 flex items-start justify-between gap-4">
        <div className="text-sm text-gray-600 max-w-xl">
          When enabled, this enterprise agency automatically receives up to {effectiveMax} exclusively-assigned
          active CRM leads in their workspace (My Sites). Leads assigned here are hidden from every
          other agency. Turn this off to stop assigning new leads to this agency.
        </div>
        <button
          type="button"
          onClick={handleToggle}
          disabled={loading}
          role="switch"
          aria-checked={enabled}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-wait ${enabled ? 'bg-indigo-600' : 'bg-gray-300'}`}
          title={enabled ? 'Disable Agency Leads' : 'Enable Agency Leads'}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
      {enabled && (
        <div className="px-4 pb-4 border-t pt-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-gray-600 max-w-md">
              <div className="font-medium text-gray-800">Queue size</div>
              Maximum number of exclusively-assigned active leads this agency holds at once.
              Default is {AGENCY_LEADS_DEFAULT_MAX}. {isOverridden && <span className="text-indigo-600 font-medium">Currently overridden.</span>}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={AGENCY_LEADS_MIN}
                max={AGENCY_LEADS_MAX}
                value={maxInput}
                onChange={(e) => setMaxInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleMaxSave(); }}
                disabled={savingMax}
                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={handleMaxSave}
                disabled={savingMax || String(effectiveMax) === maxInput}
                className="px-3 py-1 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
              {isOverridden && (
                <button
                  type="button"
                  onClick={() => saveMax(null)}
                  disabled={savingMax}
                  className="px-3 py-1 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  title="Reset to default"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Block Management Component
const BlockManagement = ({ account, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const handleBlock = async () => {
    if (!window.confirm('Are you sure you want to block this account? All users in this account will be unable to access the platform.')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/accounts/${account.id}/block`, {
        reason: blockReason || null
      });
      toast.success('Account has been blocked');
      setShowBlockModal(false);
      setBlockReason('');
      onUpdate();
    } catch (error) {
      console.error('Error blocking account:', error);
      toast.error(error.response?.data?.error || 'Failed to block account');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async () => {
    if (!window.confirm('Are you sure you want to unblock this account?')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/accounts/${account.id}/unblock`);
      toast.success('Account has been unblocked');
      onUpdate();
    } catch (error) {
      console.error('Error unblocking account:', error);
      toast.error(error.response?.data?.error || 'Failed to unblock account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-lg shadow mb-8 ${account.is_blocked ? 'bg-red-50 border-2 border-red-300' : 'bg-white'}`}>
      <div className="p-4 border-b flex items-center gap-2">
        <NoSymbolIcon className={`h-5 w-5 ${account.is_blocked ? 'text-red-600' : 'text-gray-400'}`} />
        <h2 className="text-lg font-semibold">Account Access</h2>
        {account.is_blocked && (
          <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded-full">
            BLOCKED
          </span>
        )}
      </div>
      <div className="p-4">
        {account.is_blocked ? (
          <div>
            <div className="mb-4 p-3 bg-red-100 border border-red-200 rounded-lg">
              <div className="text-sm text-red-800">
                <strong>This account is blocked.</strong> All users in this account cannot access the platform.
              </div>
              {account.blocked_at && (
                <div className="text-xs text-red-600 mt-1">
                  Blocked on: {new Date(account.blocked_at).toLocaleString()}
                </div>
              )}
              {account.blocked_reason && (
                <div className="text-sm text-red-700 mt-2">
                  <strong>Reason:</strong> {account.blocked_reason}
                </div>
              )}
            </div>
            <button
              onClick={handleUnblock}
              disabled={loading}
              className="btn-primary flex items-center gap-2"
            >
              <LockOpenIcon className="h-4 w-4" />
              {loading ? 'Unblocking...' : 'Unblock Account'}
            </button>
          </div>
        ) : (
          <div>
            {showBlockModal ? (
              <div className="p-4 bg-gray-50 rounded-lg border">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for blocking (optional)
                  </label>
                  <textarea
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="Enter reason for blocking this account..."
                    className="input-field w-full"
                    rows={3}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleBlock}
                    disabled={loading}
                    className="btn-danger flex items-center gap-2"
                  >
                    <NoSymbolIcon className="h-4 w-4" />
                    {loading ? 'Blocking...' : 'Confirm Block'}
                  </button>
                  <button
                    onClick={() => {
                      setShowBlockModal(false);
                      setBlockReason('');
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  This account has normal access. Blocking will prevent all users from accessing the platform.
                </div>
                <button
                  onClick={() => setShowBlockModal(true)}
                  className="btn-secondary text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <NoSymbolIcon className="h-4 w-4" />
                  Block Account
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AccountDetail = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [members, setMembers] = useState([]);
  // Which member's security panel is open (password reset / sessions / 2FA).
  const [securityUser, setSecurityUser] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [legacySubscriptions, setLegacySubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', tier: '' });
  const [editingLifetimeSites, setEditingLifetimeSites] = useState(false);
  const [lifetimeSitesValue, setLifetimeSitesValue] = useState(0);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ userId: '', role: 'editor' });
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const searchDebounceRef = useRef(null);
  const dropdownRef = useRef(null);

  const searchUsers = useCallback(async (query) => {
    if (query.trim().length < 2) {
      setUserSearchResults([]);
      setShowUserDropdown(false);
      return;
    }
    try {
      setUserSearchLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/users/search`, {
        params: { q: query }
      });
      const results = response.data.data || [];
      setUserSearchResults(results);
      setShowUserDropdown(results.length > 0);
    } catch (error) {
      console.error('Error searching users:', error);
      setUserSearchResults([]);
      setShowUserDropdown(false);
    } finally {
      setUserSearchLoading(false);
    }
  }, []);

  const handleUserSearchChange = (e) => {
    const value = e.target.value;
    setUserSearchQuery(value);
    setSelectedUser(null);
    setAddMemberForm(prev => ({ ...prev, userId: '' }));

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => searchUsers(value), 300);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setAddMemberForm(prev => ({ ...prev, userId: user.id }));
    setUserSearchQuery(`${user.name || user.email} (${user.email})`);
    setShowUserDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAccountDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/accounts/${accountId}`);
      const data = response.data.data;
      
      setAccount(data.account);
      setMembers(data.members || []);
      setWebsites(data.websites || []);
      setInvitations(data.invitations || []);
      setLegacySubscriptions(data.legacySubscriptions || []);
      setEditForm({ name: data.account.name || '', tier: data.account.tier || 'free' });
      setLifetimeSitesValue(data.account.lifetime_sites_created || 0);
    } catch (error) {
      console.error('Error fetching account details:', error);
      toast.error('Failed to fetch account details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccountDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const handleUpdateAccount = async () => {
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/accounts/${accountId}`, editForm);
      toast.success('Account updated successfully');
      setEditing(false);
      fetchAccountDetails();
    } catch (error) {
      console.error('Error updating account:', error);
      toast.error(error.response?.data?.error || 'Failed to update account');
    }
  };

  const handleUpdateLifetimeSites = async () => {
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/accounts/${accountId}`, {
        lifetime_sites_created: lifetimeSitesValue
      });
      toast.success('Lifetime sites created updated successfully');
      setEditingLifetimeSites(false);
      fetchAccountDetails();
    } catch (error) {
      console.error('Error updating lifetime sites created:', error);
      toast.error(error.response?.data?.error || 'Failed to update lifetime sites created');
    }
  };

  const handleChangeMemberRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/accounts/${accountId}/members/${userId}`, {
        role: newRole
      });
      toast.success('Member role updated');
      fetchAccountDetails();
    } catch (error) {
      console.error('Error updating member role:', error);
      toast.error(error.response?.data?.error || 'Failed to update member role');
    }
  };

  const handleChangeCrmRole = async (userId, websiteId, crmRole) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/api/admin/accounts/${accountId}/members/${userId}/crm-role`,
        { websiteId, crmRole }
      );
      toast.success('CRM role updated');
      fetchAccountDetails();
    } catch (error) {
      console.error('Error updating CRM role:', error);
      toast.error(error.response?.data?.error || 'Failed to update CRM role');
    }
  };

  const handleRemoveMember = async (userId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from this account?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/accounts/${accountId}/members/${userId}`);
      toast.success('Member removed from account');
      fetchAccountDetails();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(error.response?.data?.error || 'Failed to remove member');
    }
  };

  const resetAddMemberState = () => {
    setShowAddMember(false);
    setAddMemberForm({ userId: '', role: 'editor' });
    setUserSearchQuery('');
    setUserSearchResults([]);
    setSelectedUser(null);
    setShowUserDropdown(false);
  };

  const handleAddMember = async () => {
    if (!addMemberForm.userId) {
      toast.error('Please select a user');
      return;
    }
    
    try {
      await axios.post(`${API_BASE_URL}/api/admin/accounts/${accountId}/members`, addMemberForm);
      toast.success('Member added to account');
      resetAddMemberState();
      fetchAccountDetails();
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error.response?.data?.error || 'Failed to add member');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleting) return;
    if (!window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      // Each request deletes a slice. Hundreds of multi-MB website versions
      // cannot be removed in one statement (the 60s query timeout rolls the
      // whole delete back), and one long request is cut off by the proxy.
      let finished = false;
      let versionBatch = null;
      for (let attempt = 0; attempt < 2000; attempt++) {
        try {
          const response = await axios.delete(
            `${API_BASE_URL}/api/admin/accounts/${accountId}`,
            versionBatch ? { params: { versionBatch } } : undefined
          );
          if (response.data?.versionBatch) versionBatch = response.data.versionBatch;
          if (response.data?.done !== false) {
            finished = true;
            break;
          }
          if (response.data?.status === 'in_progress') {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } else if (
            !response.data?.versionsDeleted
            && !response.data?.analyticsDeleted
            && !response.data?.chunksDeleted
            && !response.data?.websitesDeleted
          ) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        } catch (error) {
          if (error.response?.status === 404) {
            finished = true;
            break;
          }
          throw error;
        }
      }
      if (!finished) {
        toast.error('Account deletion is still in progress. Click Delete Account again to continue.');
        return;
      }
      toast.success('Account deleted successfully');
      navigate('/accounts');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.error || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!account) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Not Found</h2>
          <Link to="/accounts" className="text-primary-600 hover:text-primary-700">
            Back to Accounts
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/accounts"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Accounts
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              {editing ? (
                <div className="flex items-center gap-4">
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="text-3xl font-bold border-b-2 border-primary-500 focus:outline-none"
                    placeholder="Account name"
                  />
                  {!isBundleAccount(account) && (
                    <select
                      value={editForm.tier}
                      onChange={(e) => setEditForm({ ...editForm, tier: e.target.value })}
                      className="input-field"
                    >
                      <option value="trial">Trial</option>
                      <option value="pro">Pro</option>
                      <option value="ecommerce">E-commerce</option>
                      <option value="landing_page">Landing Page</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  )}
                  <button onClick={handleUpdateAccount} className="p-2 text-green-600 hover:bg-green-50 rounded">
                    <CheckIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => setEditing(false)} className="p-2 text-gray-600 hover:bg-gray-50 rounded">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <h1 className="text-3xl font-bold text-gray-900">{account.name || 'Unnamed Account'}</h1>
                  <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getAccountPlanBadgeClass(account)}`}>
                    {getAccountPlanLabel(account)}
                  </span>
                  <button onClick={() => setEditing(true)} className="p-2 text-gray-600 hover:bg-gray-50 rounded">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
              <p className="text-gray-500 mt-1">
                Created {new Date(account.created_at).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="btn-danger flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <TrashIcon className="h-4 w-4" />
              {deleting ? 'Deleting…' : 'Delete Account'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Members</div>
            <div className="text-2xl font-bold text-gray-900">{account.member_count}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Websites</div>
            <div className="text-2xl font-bold text-gray-900">{account.website_count}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Published</div>
            <div className="text-2xl font-bold text-green-600">{account.published_count}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Lifetime Sites Created</div>
            {editingLifetimeSites ? (
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="number"
                  min="0"
                  value={lifetimeSitesValue}
                  onChange={(e) => setLifetimeSitesValue(parseInt(e.target.value, 10) || 0)}
                  className="w-20 px-2 py-1 text-xl font-bold border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button 
                  onClick={handleUpdateLifetimeSites} 
                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                  title="Save"
                >
                  <CheckIcon className="h-5 w-5" />
                </button>
                <button 
                  onClick={() => {
                    setEditingLifetimeSites(false);
                    setLifetimeSitesValue(account.lifetime_sites_created || 0);
                  }} 
                  className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                  title="Cancel"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-gray-900">{account.lifetime_sites_created}</div>
                <button 
                  onClick={() => setEditingLifetimeSites(true)} 
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded"
                  title="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Engagement Bundle — sell / activate / manage the pre-paid bundle.
            Self-hides when FEATURE_ENGAGEMENT_BUNDLE is off (routes 404). */}
        <BundleSection accountId={accountId} />

        {/* Trial Management - Only show for trial tier accounts */}
        {(account.tier === 'trial' || account.tier === 'free') && (
          <TrialManagement 
            account={account} 
            onUpdate={fetchAccountDetails} 
          />
        )}

        {/* Agency Leads opt-in — enterprise accounts only */}
        {account.tier === 'enterprise' && (
          <AgencyLeadsCard account={account} onUpdate={fetchAccountDetails} />
        )}

        {/* Block Management */}
        <BlockManagement 
          account={account} 
          onUpdate={fetchAccountDetails} 
        />

        {/* Members / Users */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Users ({members.length})</h2>
            <button
              onClick={() => setShowAddMember(true)}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <UserPlusIcon className="h-4 w-4" />
              Add User
            </button>
          </div>
          
          {showAddMember && (
            <div className="p-4 bg-gray-50 border-b">
              <div className="flex items-center gap-4">
                <div className="relative flex-1" ref={dropdownRef}>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={userSearchQuery}
                      onChange={handleUserSearchChange}
                      onFocus={() => userSearchResults.length > 0 && setShowUserDropdown(true)}
                      className="input-field w-full pl-9"
                      autoFocus
                    />
                    {userSearchLoading && (
                      <ArrowPathIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                    )}
                  </div>
                  {showUserDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {userSearchResults.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleSelectUser(user)}
                          className="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                        >
                          {user.picture_url ? (
                            <img src={user.picture_url} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-sm font-medium text-gray-600">
                              {(user.name || user.email || '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{user.name || 'No name'}</div>
                            <div className="text-xs text-gray-500 truncate">{user.email}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <select
                  value={addMemberForm.role}
                  onChange={(e) => setAddMemberForm({ ...addMemberForm, role: e.target.value })}
                  className="input-field"
                >
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                  <option value="crm">BOS Only</option>
                </select>
                <button onClick={handleAddMember} className="btn-primary">Add</button>
                <button onClick={resetAddMemberState} className="btn-secondary">Cancel</button>
              </div>
              {selectedUser && (
                <div className="mt-2 flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded px-3 py-1.5 w-fit">
                  <CheckIcon className="h-3.5 w-3.5" />
                  Selected: <span className="font-medium">{selectedUser.name || selectedUser.email}</span>
                  <span className="text-green-600">({selectedUser.id})</span>
                </div>
              )}
              {!selectedUser && (
                <p className="text-xs text-gray-500 mt-2">
                  Search for a user by name or email. They must already have a Zappy account.
                </p>
              )}
            </div>
          )}
          
          {/* Table Header */}
          <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b text-sm font-medium text-gray-500">
            <div className="col-span-3">User</div>
            <div className="col-span-2">Account Role</div>
            <div className="col-span-3">BOS Role</div>
            <div className="col-span-2">Joined Account</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          
          <div className="divide-y">
            {members.map((member) => (
              <div key={member.id} className="p-4 md:grid md:grid-cols-12 md:gap-4 md:items-start">
                {/* User Info */}
                <div className="col-span-3 flex items-center gap-3 mb-3 md:mb-0">
                  {member.pictureUrl ? (
                    <img src={member.pictureUrl} alt="" className="h-10 w-10 rounded-full flex-shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-500 font-semibold">
                        {(member.name || member.email || '?')[0].toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {member.name || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-500 truncate flex items-center gap-1">
                      <EnvelopeIcon className="h-3 w-3 flex-shrink-0" />
                      {member.email}
                    </div>
                    {member.phoneNumber && (
                      <div className="text-sm text-gray-500 truncate flex items-center gap-1">
                        <PhoneIcon className="h-3 w-3 flex-shrink-0" />
                        {member.phoneNumber}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 truncate mt-0.5">
                      ID: {member.userId}
                    </div>
                  </div>
                </div>
                
                {/* Account Role */}
                <div className="col-span-2 mb-2 md:mb-0">
                  <select
                    value={member.role}
                    onChange={(e) => handleChangeMemberRole(member.userId, e.target.value)}
                    className={`input-field text-sm w-full ${
                      member.role === 'admin'
                        ? 'bg-purple-50 border-purple-200'
                        : member.role === 'crm'
                          ? 'bg-emerald-50 border-emerald-200'
                          : ''
                    }`}
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="crm">BOS Only</option>
                  </select>
                </div>

                {/* CRM Role (per website) */}
                <div className="col-span-3 mb-2 md:mb-0 space-y-1.5">
                  {member.isZappyAdmin ? (
                    <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5">
                      Zappy Staff — full CRM manager (overrides stored roles)
                    </div>
                  ) : (member.crmRoles || []).length === 0 ? (
                    <span className="text-sm text-gray-400">No websites</span>
                  ) : (
                    member.crmRoles.map((crmSite) => (
                      <div key={crmSite.websiteId} className="flex items-center gap-2 min-w-0">
                        <span
                          className="text-xs text-gray-500 truncate max-w-[7rem]"
                          title={crmSite.websiteName}
                        >
                          {crmSite.websiteName}
                        </span>
                        <select
                          value={crmSite.effectiveRole || crmSite.storedRole}
                          onChange={(e) => handleChangeCrmRole(member.userId, crmSite.websiteId, e.target.value)}
                          className={`input-field text-xs flex-1 min-w-0 ${
                            crmSite.effectiveRole === 'manager'
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-amber-50 border-amber-200'
                          }`}
                          disabled={crmSite.overriddenByZappyAdmin}
                        >
                          <option value="manager">BOS Admin</option>
                          <option value="agent">BOS Agent</option>
                        </select>
                        {crmSite.excluded && (
                          <span className="text-[10px] text-orange-700 whitespace-nowrap">Excluded</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
                
                {/* Joined Date */}
                <div className="col-span-2 mb-2 md:mb-0">
                  <div className="text-sm text-gray-600 flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                    {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                
                {/* Status Badges */}
                <div className="col-span-1 mb-2 md:mb-0 flex flex-wrap gap-1">
                  {member.loginProvider && (
                    <LoginProviderBadge provider={member.loginProvider} />
                  )}
                  {member.isZappyAdmin && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                      <ShieldCheckIcon className="h-3 w-3" />
                      Zappy Staff
                    </span>
                  )}
                  {member.role === 'admin' && (
                    <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                      Account Admin
                    </span>
                  )}
                  {member.role === 'crm' && (
                    <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 rounded-full">
                      BOS Only
                    </span>
                  )}
                  {!member.onboardingCompleted && (
                    <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                      Onboarding Pending
                    </span>
                  )}
                </div>
                
                {/* Actions */}
                <div className="col-span-1 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setSecurityUser(member)}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                    title="Security: password reset, sessions, 2FA"
                  >
                    <ShieldCheckIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleRemoveMember(member.userId, member.name || member.email)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Remove from account"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            {members.length === 0 && (
              <div className="p-8 text-center text-gray-500">No users found in this account</div>
            )}
          </div>
        </div>

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">Pending Invitations ({invitations.filter(i => i.status === 'pending').length})</h2>
            </div>
            <div className="divide-y">
              {invitations.map((invitation) => (
                <div key={invitation.id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{invitation.email}</div>
                    <div className="text-sm text-gray-500">
                      Role: {invitation.role}
                      {invitation.crmRole && (
                        <> • CRM: {invitation.crmRole === 'manager' ? 'Admin' : 'Agent'}
                          {invitation.crmWebsiteName ? ` (${invitation.crmWebsiteName})` : ''}
                        </>
                      )}
                      {' • '}
                      Status: {invitation.status} • Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    invitation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    invitation.status === 'accepted' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {invitation.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Websites */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Websites ({websites.length})</h2>
          </div>
          <div className="divide-y">
            {websites.map((website) => {
              const siteType = website.siteType || 'marketing';
              return (
              <div key={website.id} className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Link to={`/websites/${website.id}`} className="font-medium text-gray-900 hover:text-primary-600">
                      {website.name || 'Unnamed Website'}
                    </Link>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      siteType === 'ecommerce' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {siteType}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {website.industry} • Created {new Date(website.createdAt).toLocaleDateString()}
                  </div>
                  <SubscriptionSummary subscription={website.subscription} siteType={siteType} />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    website.deploymentStatus === 'deployed' ? 'bg-green-100 text-green-800' :
                    website.deploymentStatus === 'deploying' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {website.deploymentStatus || website.status}
                  </span>
                  {website.renderUrl && (
                    <a 
                      href={website.renderUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 text-sm"
                    >
                      View Site
                    </a>
                  )}
                </div>
              </div>
              );
            })}
            {websites.length === 0 && (
              <div className="p-8 text-center text-gray-500">No websites found</div>
            )}
          </div>
        </div>

        {/* Legacy account-wide subscriptions not linked to a website */}
        {legacySubscriptions.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg shadow mb-8">
            <div className="p-4 border-b border-amber-200">
              <h2 className="text-lg font-semibold text-amber-900">
                Unlinked Subscriptions ({legacySubscriptions.length})
              </h2>
              <p className="text-sm text-amber-800 mt-1">
                These legacy account-level subscriptions are not tied to a website. Link each one from the subscription detail page.
              </p>
            </div>
            <div className="divide-y divide-amber-200">
              {legacySubscriptions.map((sub) => (
                <div key={sub.id} className="p-4">
                  <Link
                    to={`/subscriptions/${sub.id}`}
                    className="font-medium text-primary-700 hover:text-primary-900"
                  >
                    {formatPlanLabel(sub)}
                  </Link>
                  <div className="mt-1 flex flex-wrap gap-3 text-sm text-amber-900">
                    <span>Status: {sub.status}</span>
                    {sub.billing_period && <span>{sub.billing_period}</span>}
                    {sub.amount != null && sub.currency && (
                      <span>{sub.currency} {sub.amount}</span>
                    )}
                    {sub.payment_provider && <span>{sub.payment_provider}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {securityUser && (
          <UserSecurityModal
            user={securityUser}
            onClose={() => setSecurityUser(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AccountDetail;

